import {
  COMPASS_POINTS,
  FUEL_TYPES,
  LUBE_TYPES,
  type NextOperation,
  nextOperationOptions,
  VISIBILITY_GRADES,
  type VoyageReportKind,
} from '@felix/domain';
import { ActionIcon, Button, Radio, Select } from '@mantine/core';
import {
  IconArrowRight,
  IconDroplet,
  IconGasStation,
  IconPlus,
  IconRoute,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect } from 'react';
import { useController, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NumberField, SelectField, TextField, useFieldError } from './fields.js';
import { FuelLedgerNote, useFuelLedger } from './fuel-ledger.js';
import le from './LegEventScreen.module.css';
import noon from './NoonReportScreen.module.css';
import { Group, LocalDateTimeField } from './NoonSections.js';
import type { NextDue } from './next-due.js';
import { nextOperationKeys } from './next-operation-text.js';
import classes from './ReportSections.module.css';
import { NextDueText } from './VoyageContextNotices.js';

/**
 * Sefer raporlarının ortak bölümleri (ADR 0057 §3, §5): römorkör, ROB (yakıt, yağ, tatlı su;
 * metrik ton), hava (Beaufort, Douglas), kişiler, gemi durumu ve Next Operation. Alanlar
 * `data` altında isteğe bağlıdır; hangi türde hangisinin gösterileceği `REPORT_SECTIONS`'tadır.
 * Formun durumu React Hook Form'dadır; doğrulama `parseVoyageReport`.
 */

const useK = () => {
  const { t } = useTranslation();
  return (key: string, options?: Record<string, unknown>) =>
    t(`voyage.sections.${key}`, options ?? {});
};

const round1 = (value: number) => Math.round(value * 10) / 10;

/** Değer girilmemiş (boş, `null` ya da yalnız boş alanlı nesne/dizi). */
const blank = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value)
    ? value.every(blank)
    : typeof value === 'object' && Object.values(value).every(blank));

/** Salt okunur raporda boş bölüm gösterilmez (eski raporlar; ADR 0057 §3). */
function useHidden(name: string, readOnly: boolean): boolean {
  const value = useWatch({ name }) as unknown;
  return readOnly && blank(value);
}

/* ---------- Römorkör ---------- */

export function TugsSection({ readOnly }: { readOnly: boolean }) {
  const k = useK();
  const { fields, append, remove } = useFieldArray({ name: 'data.tugs' });
  const legacy = useWatch({ name: 'data.tugDetails' }) as unknown;
  if (readOnly && fields.length === 0 && blank(legacy)) return null;
  return (
    <Group title={k('tugs.title')} className={le.wide}>
      <div className={classes.rows}>
        {fields.length === 0 ? <p className={noon.muted}>{k('tugs.none')}</p> : null}
        {fields.map((row, index) => (
          <fieldset
            key={row.id}
            className={classes.tug}
            aria-label={k('tugs.row', { no: index + 1 })}
          >
            <span className={classes.rowNo} aria-hidden>
              {index + 1}
            </span>
            <TextField
              name={`data.tugs.${index}.name`}
              label={k('tugs.name')}
              required
              readOnly={readOnly}
            />
            <NumberField
              name={`data.tugs.${index}.bollardPullT`}
              label={k('tugs.bollardPull')}
              unit="t"
              decimals={0}
              readOnly={readOnly}
            />
            {readOnly ? (
              <span />
            ) : (
              <ActionIcon
                variant="subtle"
                color="gray"
                className={classes.remove}
                aria-label={k('tugs.remove', { no: index + 1 })}
                onClick={() => remove(index)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            )}
            <LocalDateTimeField
              name={`data.tugs.${index}.alongside`}
              label={k('tugs.alongside')}
              readOnly={readOnly}
            />
            <LocalDateTimeField
              name={`data.tugs.${index}.castOff`}
              label={k('tugs.castOff')}
              readOnly={readOnly}
            />
          </fieldset>
        ))}
        {!readOnly && fields.length < 10 ? (
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            className={le.addRow}
            onClick={() => append({ name: '', bollardPullT: null, alongside: null, castOff: null })}
          >
            {k('tugs.add')}
          </Button>
        ) : null}
        {typeof legacy === 'string' && legacy.trim() ? (
          <p className={noon.muted}>{k('tugs.legacy', { text: legacy })}</p>
        ) : null}
      </div>
    </Group>
  );
}

/* ---------- ROB: yakıt, yağ, tatlı su (MT) ---------- */

type RobLine = {
  previousMt?: unknown;
  consumptionMt?: unknown;
  rob?: unknown;
};

/**
 * Tüketim boşsa önceki − ROB ile doldurulur (ikmal yoksa; ADR 0057 §3); elle düzeltilebilir.
 * Negatif fark (ikmal) önerilmez.
 */
function useConsumptionFill(name: string, readOnly: boolean) {
  const { setValue, getValues } = useFormContext();
  const watched = useWatch({ name });
  useEffect(() => {
    if (readOnly) return;
    const lines = (Array.isArray(watched) ? watched : watched ? [watched] : []) as RobLine[];
    lines.forEach((line, index) => {
      const path = Array.isArray(watched)
        ? `${name}.${index}.consumptionMt`
        : `${name}.consumptionMt`;
      if (typeof line.previousMt !== 'number' || typeof line.rob !== 'number') return;
      if (getValues(path) !== null && getValues(path) !== undefined) return;
      const used = round1(line.previousMt - line.rob);
      if (used >= 0) setValue(path, used, { shouldDirty: true });
    });
  }, [watched, name, readOnly, setValue, getValues]);
}

function RobHead({ first, today }: { first: string; today: boolean }) {
  const k = useK();
  return (
    <div className={classes.robHead} aria-hidden>
      <span>{first}</span>
      <span>{k('rob.previous')}</span>
      <span>{today ? k('rob.sinceLast') : k('rob.consumed')}</span>
      {today ? <span>{k('rob.today')}</span> : null}
      <span>{k('rob.rob')}</span>
      <span />
    </div>
  );
}

/**
 * Önceki, tüketilen, ROB; birim (MT) bölüm başlığında, dar ekranda değer kesilmesin. Günlük
 * raporda tüketim "son rapordan beri" ve ayrıca "bugün" (ADR 0059 §3).
 */
function RobNumbers({
  base,
  label,
  readOnly,
  today,
}: {
  base: string;
  label: string;
  readOnly: boolean;
  today: boolean;
}) {
  const k = useK();
  return (
    <>
      <NumberField
        name={`${base}.previousMt`}
        label={`${label} ${k('rob.previous')}`}
        hideLabel
        decimals={1}
        readOnly={readOnly}
      />
      <NumberField
        name={`${base}.consumptionMt`}
        label={`${label} ${today ? k('rob.sinceLast') : k('rob.consumed')}`}
        hideLabel
        decimals={1}
        readOnly={readOnly}
      />
      {today ? (
        <NumberField
          name={`${base}.todayMt`}
          label={`${label} ${k('rob.today')}`}
          hideLabel
          decimals={1}
          readOnly={readOnly}
        />
      ) : null}
      <NumberField
        name={`${base}.rob`}
        label={`${label} ${k('rob.rob')}`}
        hideLabel
        decimals={1}
        required
        readOnly={readOnly}
      />
    </>
  );
}

function RobRows({
  name,
  keyName,
  options,
  readOnly,
  first,
  addLabel,
  icon,
  today,
}: {
  name: string;
  keyName: 'fuel' | 'lube';
  options: { value: string; label: string }[];
  readOnly: boolean;
  first: string;
  addLabel: string;
  icon: React.ReactNode;
  today: boolean;
}) {
  const k = useK();
  const { fields, append, remove } = useFieldArray({ name });
  const values = (useWatch({ name }) ?? []) as Record<string, unknown>[];
  useConsumptionFill(name, readOnly);
  const used = new Set(values.map((row) => row[keyName]));
  if (readOnly && fields.length === 0) return null;
  return (
    <div className={classes.robBlock} data-today={today || undefined}>
      {fields.length > 0 ? <RobHead first={first} today={today} /> : null}
      {fields.map((row, index) => {
        const current = values[index]?.[keyName];
        const label =
          typeof current === 'string'
            ? (options.find((o) => o.value === current)?.label ?? current)
            : `${first} ${index + 1}`;
        return (
          <div key={row.id} className={classes.robRow}>
            <SelectField
              name={`${name}.${index}.${keyName}`}
              label={first}
              hideLabel
              ariaLabel={`${first} ${index + 1}`}
              required
              readOnly={readOnly}
              options={options.filter((o) => o.value === current || !used.has(o.value))}
            />
            <RobNumbers base={`${name}.${index}`} label={label} readOnly={readOnly} today={today} />
            {readOnly ? (
              <span />
            ) : (
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={k('rob.remove', { name: label })}
                onClick={() => remove(index)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            )}
          </div>
        );
      })}
      {readOnly || fields.length >= options.length ? null : (
        <Button
          size="xs"
          variant="light"
          leftSection={icon}
          className={le.addRow}
          onClick={() =>
            append({
              [keyName]: null,
              previousMt: null,
              consumptionMt: null,
              ...(today ? { todayMt: null } : {}),
              rob: null,
            })
          }
        >
          {addLabel}
        </Button>
      )}
    </div>
  );
}

/**
 * ROB tablosu: yakıt türleri ve yağlar satır satır, tatlı su tek satır; hepsi metrik ton.
 * `today`: günlük raporda "bugün" sütunu (ADR 0059 §3).
 */
export function RobSection({ readOnly, today = false }: { readOnly: boolean; today?: boolean }) {
  const k = useK();
  const { t } = useTranslation();
  // Defteri açık gemide yakıt ROB'u defterden, gönderimde yazılır (ADR 0117 §2.4)
  const ledger = useFuelLedger();
  const freshWater = useWatch({ name: 'data.freshWater' }) as RobLine | null | undefined;
  const { setValue } = useFormContext();
  useConsumptionFill('data.freshWater', readOnly);
  const showWater = !readOnly || (freshWater && typeof freshWater.rob === 'number');
  const noFuel = useHidden('data.rob', readOnly);
  const noLube = useHidden('data.lubes', readOnly);
  if (noFuel && noLube && !showWater) return null;
  return (
    <Group title={k('rob.title')} className={le.wide}>
      <FuelLedgerNote />
      <RobRows
        name="data.rob"
        keyName="fuel"
        first={k('rob.fuel')}
        addLabel={t('voyage.event.addFuel')}
        icon={<IconGasStation size={14} />}
        readOnly={readOnly || ledger.active}
        today={today}
        options={FUEL_TYPES.map((value) => ({ value, label: value }))}
      />
      <RobRows
        name="data.lubes"
        keyName="lube"
        first={k('rob.lube')}
        addLabel={k('rob.addLube')}
        icon={<IconDroplet size={14} />}
        readOnly={readOnly}
        today={today}
        options={LUBE_TYPES.map((value) => ({ value, label: k(`lubes.${value}`) }))}
      />
      {showWater ? (
        <div className={classes.robBlock} data-today={today || undefined}>
          <RobHead first={k('rob.water')} today={today} />
          {freshWater ? (
            <div className={classes.robRow}>
              <span className={classes.robName}>{k('rob.freshWater')}</span>
              <RobNumbers
                base="data.freshWater"
                label={k('rob.freshWater')}
                readOnly={readOnly}
                today={today}
              />
              {readOnly ? (
                <span />
              ) : (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={k('rob.remove', { name: k('rob.freshWater') })}
                  onClick={() => setValue('data.freshWater', null, { shouldDirty: true })}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              )}
            </div>
          ) : (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconDroplet size={14} />}
              className={le.addRow}
              onClick={() =>
                setValue(
                  'data.freshWater',
                  { previousMt: null, consumptionMt: null, rob: null },
                  { shouldDirty: true },
                )
              }
            >
              {k('rob.addWater')}
            </Button>
          )}
        </div>
      ) : null}
      <p className={noon.hint}>{k('rob.note')}</p>
    </Group>
  );
}

/* ---------- Hava ---------- */

/** Sayı değerli seçim (Beaufort, Douglas): değer sayı olarak saklanır. */
function NumberSelectField({
  name,
  label,
  options,
  readOnly,
}: {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  readOnly: boolean;
}) {
  const { field } = useController({ name });
  const error = useFieldError(name);
  return (
    <Select
      label={label}
      labelProps={{ title: label }}
      data={options}
      value={typeof field.value === 'number' ? String(field.value) : null}
      onChange={(value) => field.onChange(value === null ? null : Number(value))}
      onBlur={field.onBlur}
      readOnly={readOnly}
      clearable={!readOnly}
      error={error ?? null}
      {...(readOnly ? { rightSection: null } : {})}
      classNames={{
        input: [noon.input, readOnly ? noon.locked : ''].join(' '),
        label: noon.label ?? '',
      }}
    />
  );
}

export function WeatherSection({ readOnly }: { readOnly: boolean }) {
  const k = useK();
  const hidden = useHidden('data.weather', readOnly);
  const range = (to: number, list: string) =>
    Array.from({ length: to + 1 }, (_, value) => ({
      value: String(value),
      label: `${value} – ${k(`${list}.${value}`)}`,
    }));
  if (hidden) return null;
  return (
    <Group title={k('weather.title')}>
      <div className={[noon.pair, le.fieldPair].join(' ')}>
        <SelectField
          name="data.weather.windDirection"
          label={k('weather.windDirection')}
          readOnly={readOnly}
          options={COMPASS_POINTS.map((value) => ({ value, label: value }))}
        />
        <NumberSelectField
          name="data.weather.beaufort"
          label={k('weather.beaufort')}
          readOnly={readOnly}
          options={range(12, 'beaufortScale')}
        />
        <NumberSelectField
          name="data.weather.seaState"
          label={k('weather.seaState')}
          readOnly={readOnly}
          options={range(9, 'douglasScale')}
        />
        <SelectField
          name="data.weather.visibility"
          label={k('weather.visibility')}
          readOnly={readOnly}
          options={VISIBILITY_GRADES.map((value) => ({
            value,
            label: k(`visibilityGrades.${value}`),
          }))}
        />
        <NumberField
          name="data.weather.airTempC"
          label={k('weather.airTemp')}
          unit="°C"
          decimals={1}
          readOnly={readOnly}
        />
        <NumberField
          name="data.weather.seaTempC"
          label={k('weather.seaTemp')}
          unit="°C"
          decimals={1}
          readOnly={readOnly}
        />
      </div>
    </Group>
  );
}

/* ---------- Kişiler ---------- */

export function PersonsSection({ readOnly }: { readOnly: boolean }) {
  const k = useK();
  if (useHidden('data.persons', readOnly)) return null;
  return (
    <Group title={k('persons.title')}>
      <div className={[noon.pair, le.fieldPair].join(' ')}>
        <NumberField
          name="data.persons.crew"
          label={k('persons.crew')}
          decimals={0}
          readOnly={readOnly}
        />
        <NumberField
          name="data.persons.passengers"
          label={k('persons.passengers')}
          decimals={0}
          readOnly={readOnly}
        />
      </div>
    </Group>
  );
}

/* ---------- Next Operation ---------- */

/**
 * Sonraki işlem (ADR 0057 §5): türün seçenekleri; tek seçenek kendiliğinden seçilir. Gönderimde
 * zorunlu; seçim raporla kaydedilir ve sıradaki raporun türünü belirler (plan budur). Gönderilmiş
 * son akış raporunda "Şimdi aç" düğmeleri izin verilen türlerin formunu açar, hiçbir şey
 * kaydetmez; altında sıradaki rapor ve zamanı yazar (ADR 0157 §5, #2270).
 */
export function NextOperationSection({
  kind,
  readOnly,
  next,
  due = null,
}: {
  kind: VoyageReportKind;
  readOnly: boolean;
  /** Formu şimdi açma düğmeleri: izin verilen türler ve açıcı (yalnız gemide). */
  next?: { kinds: readonly VoyageReportKind[]; open: (kind: VoyageReportKind) => void } | null;
  /** Sıradaki rapor ve zamanı (ADR 0157 §5). */
  due?: NextDue | null;
}) {
  const k = useK();
  const { t } = useTranslation();
  const options = nextOperationOptions(kind);
  const { field } = useController({ name: 'data.nextOperation' });
  const error = useFieldError('data.nextOperation');
  const value = typeof field.value === 'string' ? (field.value as NextOperation) : null;
  const only = options.length === 1 ? options[0] : undefined;
  // Aşama içi raporun tek seçeneği aşamanın devamıdır: kendiliğinden seçilir
  useEffect(() => {
    if (!readOnly && only && value === null) field.onChange(only);
  }, [readOnly, only, value, field]);
  if (options.length === 0) return null;
  return (
    <Group title={k('next.title')} icon={<IconRoute size={18} />} className={le.wide}>
      <Radio.Group
        value={value}
        onChange={(next) => !readOnly && field.onChange(next)}
        label={k('next.question')}
        withAsterisk={!readOnly}
        error={error ?? null}
        classNames={{ label: noon.label ?? '' }}
      >
        <div className={classes.nextOptions}>
          {options.map((option) => {
            const text = nextOperationKeys(kind, option);
            return (
              <Radio.Card
                key={option}
                value={option}
                disabled={readOnly && value !== option}
                className={classes.nextCard}
                data-checked={value === option || undefined}
                aria-label={k(text.label)}
              >
                <Radio.Indicator size="xs" />
                <span className={classes.nextText}>
                  <strong>{k(text.label)}</strong>
                  <small>{k(text.leadsTo)}</small>
                </span>
              </Radio.Card>
            );
          })}
        </div>
      </Radio.Group>
      {next && next.kinds.length > 0 ? (
        <div className={classes.nextReports}>
          <span className={noon.label}>{k('next.nextReport')}</span>
          {next.kinds.map((nextKind) => (
            <Button
              key={nextKind}
              size="xs"
              variant="light"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => next.open(nextKind)}
            >
              {t(`voyage.kinds.${nextKind}`)}
            </Button>
          ))}
        </div>
      ) : null}
      {due ? (
        <p className={noon.hint} data-testid="next-due">
          <NextDueText due={due} />
        </p>
      ) : null}
      <p className={noon.hint}>{k('next.note')}</p>
    </Group>
  );
}
